import { create } from 'zustand';
import type { AIModel, ProjectFile } from '../../worker/types';

// Project state
interface Project {
    id: string;
    name: string;
    status: 'draft' | 'generating' | 'ready' | 'deployed' | 'error';
    previewUrl?: string;
    deployedUrl?: string;
}

// File in the editor
interface EditorFile {
    path: string;
    content: string;
    isDirty: boolean;
}

// Main store state
interface AppState {
    // Projects management
    project: Project | null;
    projects: Project[];
    setProject: (project: Project | null) => void;
    fetchProjects: () => Promise<void>;
    createProject: (name?: string) => Promise<void>;
    loadProject: (id: string) => Promise<void>;
    updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
    deleteProject: (id: string) => Promise<void>;
    bulkDeleteProjects: (ids: string[]) => Promise<void>;

    // Files
    files: Map<string, EditorFile>;
    activeFilePath: string | null;
    setActiveFile: (path: string) => void;
    updateFile: (path: string, content: string) => void;
    addFile: (path: string, content: string) => void;
    deleteFile: (path: string) => void;
    setFiles: (files: ProjectFile[]) => void;

    // Generation
    isGenerating: boolean;
    generationProgress: string;
    selectedModel: AIModel | null;
    promptHistory: any[];
    fetchPromptHistory: (projectId: string) => Promise<void>;
    setSelectedModel: (model: AIModel) => void;
    startGeneration: () => void;
    completeGeneration: (files: ProjectFile[]) => void;
    errorGeneration: (error: string) => void;
    updateGenerationProgress: (chunk: string) => void;

    // UI State
    showSidebar: boolean;
    toggleSidebar: () => void;
    previewUrl: string | null;
    setPreviewUrl: (url: string | null) => void;
    isSyncing: boolean;
    setSyncing: (syncing: boolean) => void;

    // Terminal State
    terminalLines: Array<{ content: string; stream: 'stdout' | 'stderr' }>;
    addTerminalLine: (content: string, stream: 'stdout' | 'stderr') => void;
    clearTerminal: () => void;

    // Settings
    apiKey: string | null;
    setApiKey: (key: string | null) => void;

    // WebSocket
    ws: WebSocket | null;
    connectWebSocket: (projectId: string) => void;
    disconnectWebSocket: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
    // Projects management
    project: null,
    projects: [],
    setProject: (project) => set({ project }),

    fetchProjects: async () => {
        try {
            const res = await fetch('/api/projects');
            const data = await res.json() as { projects: Project[] };
            set({ projects: data.projects });
        } catch (e) {
            console.error('Failed to fetch projects', e);
        }
    },

    createProject: async (name) => {
        try {
            const res = await fetch('/api/projects', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: name || 'New Vibe Project' }),
            });
            const data = await res.json() as { project: Project };
            set({ project: data.project, files: new Map(), activeFilePath: null });
            get().connectWebSocket(data.project.id);
            await get().fetchProjects();
        } catch (e) {
            console.error('Failed to create project', e);
        }
    },

    loadProject: async (id) => {
        try {
            const res = await fetch(`/api/projects/${id}`);
            const data = await res.json() as { project: Project; files: ProjectFile[] };
            set({ project: data.project });
            get().setFiles(data.files);
            get().connectWebSocket(id);
            get().fetchPromptHistory(id);
        } catch (e) {
            console.error('Failed to load project', e);
        }
    },

    updateProject: async (id, updates) => {
        try {
            const res = await fetch(`/api/projects/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });
            const data = await res.json() as { project: Project };
            if (get().project?.id === id) {
                set({ project: data.project });
            }
            await get().fetchProjects();
        } catch (e) {
            console.error('Failed to update project', e);
        }
    },

    deleteProject: async (id) => {
        try {
            await fetch(`/api/projects/${id}`, { method: 'DELETE' });
            if (get().project?.id === id) {
                set({ project: null, files: new Map(), activeFilePath: null });
            }
            await get().fetchProjects();
        } catch (e) {
            console.error('Failed to delete project', e);
        }
    },

    bulkDeleteProjects: async (ids) => {
        try {
            // Run all deletions in parallel
            await Promise.all(ids.map(id => fetch(`/api/projects/${id}`, { method: 'DELETE' })));

            const currentProjectId = get().project?.id;
            if (currentProjectId && ids.includes(currentProjectId)) {
                set({ project: null, files: new Map(), activeFilePath: null });
            }

            await get().fetchProjects();
        } catch (e) {
            console.error('Failed to bulk delete projects', e);
        }
    },

    // Files
    files: new Map(),
    activeFilePath: null,

    // Terminal
    terminalLines: [],
    addTerminalLine: (content, stream) => set((state) => ({
        terminalLines: [...state.terminalLines, { content, stream }].slice(-100)
    })),
    clearTerminal: () => set({ terminalLines: [] }),

    setActiveFile: (path) => set({ activeFilePath: path }),

    updateFile: (path, content) => {
        const { files, ws, setSyncing } = get();
        const file = files.get(path);
        if (file) {
            const newFiles = new Map(files);
            newFiles.set(path, { ...file, content, isDirty: true });
            set({ files: newFiles });

            // Send update via WebSocket
            if (ws?.readyState === WebSocket.OPEN) {
                setSyncing(true);
                ws.send(JSON.stringify({ type: 'file-update', path, content }));
                // Reset syncing after a short delay since WS is fast
                setTimeout(() => setSyncing(false), 500);
            }
        }
    },

    addFile: async (path, content) => {
        const { files, project, setSyncing } = get();
        const newFiles = new Map(files);
        newFiles.set(path, { path, content, isDirty: false });
        set({ files: newFiles, activeFilePath: path });

        if (project) {
            setSyncing(true);
            try {
                await fetch('/api/files', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ projectId: project.id, path, content }),
                });
            } finally {
                setSyncing(false);
            }
        }
    },

    deleteFile: async (path) => {
        const { files, activeFilePath, project, setSyncing } = get();
        const newFiles = new Map(files);
        newFiles.delete(path);
        set({
            files: newFiles,
            activeFilePath: activeFilePath === path ? null : activeFilePath
        });

        if (project) {
            setSyncing(true);
            try {
                await fetch(`/api/files/${project.id}/${encodeURIComponent(path)}`, {
                    method: 'DELETE',
                });
            } finally {
                setSyncing(false);
            }
        }
    },

    setFiles: (projectFiles) => {
        const newFiles = new Map<string, EditorFile>();
        for (const file of projectFiles) {
            if (file.type === 'file') {
                newFiles.set(file.path, { path: file.path, content: file.content, isDirty: false });
            }
        }
        const firstFile = projectFiles.find(f => f.path.endsWith('.tsx') || f.path.endsWith('.ts'));
        set({
            files: newFiles,
            activeFilePath: firstFile?.path ?? projectFiles[0]?.path ?? null
        });
    },

    // AI Generation
    isGenerating: false,
    generationProgress: '',
    selectedModel: null,
    promptHistory: [],
    fetchPromptHistory: async (projectId) => {
        try {
            const res = await fetch(`/api/ai/history/${projectId}`);
            const data = await res.json() as { history: any[] };
            set({ promptHistory: data.history });
        } catch (e) {
            console.error('Failed to fetch prompt history', e);
        }
    },

    setSelectedModel: (model) => set({ selectedModel: model }),

    startGeneration: () => set({
        isGenerating: true,
        generationProgress: ''
    }),

    updateGenerationProgress: (chunk) => set((state) => ({
        generationProgress: state.generationProgress + chunk
    })),

    completeGeneration: (files) => {
        const { setFiles, project, fetchPromptHistory } = get();
        setFiles(files);
        set({ isGenerating: false });
        if (project) {
            // Give the worker a moment to finish saving the generation
            setTimeout(() => fetchPromptHistory(project.id), 2000);
        }
    },

    errorGeneration: (error) => set({
        isGenerating: false,
        generationProgress: `Error: ${error}`
    }),

    // UI State
    showSidebar: true,
    toggleSidebar: () => set((state) => ({ showSidebar: !state.showSidebar })),
    previewUrl: null,
    setPreviewUrl: (url) => set({ previewUrl: url }),
    isSyncing: false,
    setSyncing: (isSyncing) => set({ isSyncing }),

    // Settings
    apiKey: localStorage.getItem('vibeflare_api_key'),
    setApiKey: (key) => {
        if (key) localStorage.setItem('vibeflare_api_key', key);
        else localStorage.removeItem('vibeflare_api_key');
        set({ apiKey: key });
    },

    // WebSocket
    ws: null,

    connectWebSocket: (projectId) => {
        const { ws: existingWs } = get();
        if (existingWs) {
            existingWs.close();
        }

        let reconnectAttempts = 0;
        const maxReconnectAttempts = 5;
        const baseDelay = 1000; // 1 second

        const connect = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            // Use host (includes port) for proper WebSocket connection
            const host = window.location.host;
            const ws = new WebSocket(`${protocol}//${host}/ws/${projectId}?role=editor`);

            ws.onopen = () => {
                console.log('WebSocket connected');
                reconnectAttempts = 0; // Reset on successful connection
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    const state = get();

                    switch (message.type) {
                        case 'init':
                            // Initialize state from server
                            const initFiles: ProjectFile[] = Object.entries(message.files || {}).map(
                                ([path, content]) => ({ path, content: content as string, type: 'file' as const })
                            );
                            if (initFiles.length > 0) {
                                state.setFiles(initFiles);
                            }
                            break;

                        case 'file-update':
                            state.updateFile(message.path, message.content);
                            break;

                        case 'generation-progress':
                            state.updateGenerationProgress(message.chunk);
                            break;

                        case 'generation-complete':
                            state.completeGeneration(message.files);
                            break;

                        case 'generation-error':
                            state.errorGeneration(message.error);
                            break;

                        case 'deploy-complete':
                            state.setProject({ ...state.project!, deployedUrl: message.url, status: 'deployed' });
                            break;

                        case 'preview-refresh':
                            // Trigger preview reload
                            const iframe = document.querySelector('iframe');
                            if (iframe) {
                                iframe.src = iframe.src;
                            }
                            break;

                        case 'terminal-output':
                            state.addTerminalLine(message.content, message.stream);
                            break;

                        case 'terminal-clear':
                            state.clearTerminal();
                            break;
                    }
                } catch (e) {
                    console.error('WebSocket message parse error:', e);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            ws.onclose = (event) => {
                console.log('WebSocket disconnected', event.code, event.reason);
                set({ ws: null });

                // Attempt reconnection with exponential backoff
                if (reconnectAttempts < maxReconnectAttempts && get().project?.id === projectId) {
                    reconnectAttempts++;
                    const delay = baseDelay * Math.pow(2, reconnectAttempts - 1);
                    console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
                    setTimeout(connect, delay);
                } else if (reconnectAttempts >= maxReconnectAttempts) {
                    console.error('Max WebSocket reconnection attempts reached');
                }
            };

            set({ ws });
        };

        connect();
    },

    disconnectWebSocket: () => {
        const { ws } = get();
        if (ws) {
            ws.close();
            set({ ws: null });
        }
    },
}));
